import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconSearch, IconLoader2 } from '@tabler/icons-react';
import { TeeInput } from '@/components/ui/tee-input';
import { useDebounce } from '@/hooks/useDebounce';
import { searchService, SearchResults } from '@/services/search.service';
import { SearchResultsPanel } from './SearchResultsPanel';

export function GlobalSearchBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const debouncedQuery = useDebounce(query, 300);

  // Compute total result count for keyboard nav bounds
  const totalResults = (results?.members?.length ?? 0) + (results?.leads?.length ?? 0);

  // Trigger search on debounced query change
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults(null);
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setIsOpen(true);
    setHighlightedIndex(-1);
    searchService
      .search(debouncedQuery)
      .then(data => { if (!cancelled) setResults(data); })
      .catch(() => { if (!cancelled) setResults(null); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const handleSelect = (path: string) => {
    navigate(path);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, totalResults - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      const members = results?.members ?? [];
      const leads = results?.leads ?? [];
      if (highlightedIndex < members.length) {
        handleSelect(`/members/${members[highlightedIndex]._id}/view`);
      } else {
        const leadIndex = highlightedIndex - members.length;
        handleSelect(`/leads/${leads[leadIndex]._id}/view`);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      // Retain query text per UI-SPEC interaction contract
    }
  };

  // 200ms delay on blur allows click events on result rows to fire first
  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  return (
    <div className="relative w-64">
      <TeeInput
        inputSize="s"
        placeholder={t('globalSearch.placeholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        leadingIcon={<IconSearch size={16} className="text-neutral-400" />}
        trailingIcon={
          isLoading ? (
            <IconLoader2
              size={16}
              className="animate-spin text-neutral-600"
              aria-label={t('globalSearch.loadingAriaLabel')}
            />
          ) : undefined
        }
        showTrailingIcon={isLoading}
        aria-label={t('globalSearch.inputAriaLabel')}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="global-search-results"
        aria-autocomplete="list"
      />
      {isOpen && (
        <SearchResultsPanel
          results={results}
          query={query}
          highlightedIndex={highlightedIndex}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
}
